import React, { useMemo, useState, useEffect } from 'react';
import { FarmData, FarmService } from '../../../types/FarmData';
import { useIntl, FormattedMessage } from 'react-intl';
import { StyledFarmView, RatingsTabLabel, StyledRate } from './style';
import { Menu, Spin, Rate, Tooltip } from 'antd';
import type { MenuInfo } from 'rc-menu/lib/interface';
import { Button, Icon, Spinner, Tag } from 'ui';
import PopularProducts from 'public/components/DefaultLayout/PopularProducts';
import DOMPurify from 'dompurify';
import FarmCardTabs from 'enums/farm-card-tabs';
import CompanyType from 'enums/company-type';
import Galeries from './components/Galeries';
import Partners from './components/Partners';
import Contacts from './components/Contacts';
import Deliveries from './components/Deliveries';
import Infrastructures from './components/Infrastructures';
import FarmRatings from './components/Ratings';
import { useBannerState } from 'public/context';
import Banner from 'public/components/DefaultLayout/Banner';
import SearchAndFilter from 'public/components/DefaultLayout/SearchAndFilter';
import { useNavigate } from 'react-router-dom';
import { FallbackImage } from '../../../ui/UploadImage/fallBack/FallBackImage';
import { FarmProducts } from 'types/FarmProducts';
import DefaultPagination from 'public/components/DefaultLayout/DefaultPagination';
import useMarketType from '../../../utils/useMarketType';
import PublicRoute from 'enums/publicRoutes';
import useQueryApiClient from 'utils/useQueryApiClient';
import { qualityMarkLogoClassName } from 'utils/qualityMarkHelpers';

export interface FarmViewProps {
  farm?: FarmData;
  farmProducts?: FarmProducts;
  onFarmProductsPageChange: (page: number) => void;
}

const FarmView = ({
  farm,
  farmProducts,
  onFarmProductsPageChange,
}: FarmViewProps) => {
  const FARM_PRODUCTS_PER_PAGE = 10;
  const intl = useIntl();
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>(
    FarmCardTabs.DESCRIPTION
  );
  const [farmRatingsData, setFarmRatingsData] = useState<any>(null);
  const navigate = useNavigate();
  const workingTimeData = farm?.working_time;
  const { isEmarket } = useMarketType();

  const isCooperativeSociety =
    farm?.company_type === CompanyType.COOPERATIVE_SOCIETY_KB;

  const cooperativeBadge = isCooperativeSociety ? (
    <Tooltip title={intl.formatMessage({ id: 'farms.cooperative_society' })}>
      <span className={'cooperative-badge'}>
        <img
          src={'/qualityMark/3-09 1.jpg'}
          alt={intl.formatMessage({ id: 'farms.cooperative_society' })}
        />
      </span>
    </Tooltip>
  ) : null;

  // Fetch farm ratings
  const { appendData: fetchFarmRatings } = useQueryApiClient({
    request: {
      url: `api/v2/farms/${farm?.id}/ratings`,
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setFarmRatingsData(response);
    },
  });

  useEffect(() => {
    if (farm?.id) {
      fetchFarmRatings();
    }
  }, [farm?.id]);

  let destination =
    farm?.maps_api_point ??
    farm?.actual_full_address ??
    farm?.registered_full_address ??
    '';

  const googleUrl =
    'https://www.google.com/maps/dir/?api=1&destination=' +
    encodeURIComponent(destination || '');

  const wazeUrl = farm?.maps_api_point
    ? `https://waze.com/ul?ll=${encodeURIComponent(destination || '')}&z=10`
    : 'https://waze.com/ul?navigate=yes&q=' +
      encodeURIComponent(destination || '');

  const daysOfWeek = [
    intl.formatMessage({ id: 'open_hours.Monday' }),
    intl.formatMessage({ id: 'open_hours.Tuesday' }),
    intl.formatMessage({ id: 'open_hours.Wednesday' }),
    intl.formatMessage({ id: 'open_hours.Thursday' }),
    intl.formatMessage({ id: 'open_hours.Friday' }),
    intl.formatMessage({ id: 'open_hours.Saturday' }),
    intl.formatMessage({ id: 'open_hours.Sunday' }),
  ];
  const { banners } = useBannerState();

  const products = useMemo(() => {
    return farm?.products
      ? farm?.products.map((product) => ({
          ...product,
          farm_name: farm?.brand ?? farm?.name,
        }))
      : [];
  }, [farm?.products, farm?.brand, farm?.name]);

  let address: string[] = [];
  if (farm?.actual_full_address) {
    address = farm.actual_full_address.split(',');
  } else if (farm?.registered_full_address) {
    address = farm.registered_full_address.split(',');
  }

  const renderContent = (menuItem: string) => {
    switch (menuItem) {
      case FarmCardTabs.DESCRIPTION:
        return (
          <div
            className="description"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(farm?.description || ''),
            }}
          ></div>
        );
      case FarmCardTabs.WORKING_TIME:
        return (
          <div className="farm-infrastructures">
            <Infrastructures
              infrastructures={farm?.farm_infrastructures?.map(
                (infrastructure) => ({
                  ...infrastructure,
                  open_hours: infrastructure.open_hours ?? workingTimeData,
                })
              )}
              daysOfWeek={daysOfWeek}
            />
          </div>
        );
      case FarmCardTabs.DELIVERY:
        return (
          <div className="farm-delivery">
            <Deliveries deliveries={farm?.deliveries} daysOfWeek={daysOfWeek} />
          </div>
        );
      case FarmCardTabs.GALLERY:
        return <Galeries farm={farm} />;
      case FarmCardTabs.PARTNERS:
        return <Partners farm={farm} />;
      case FarmCardTabs.CONTACTS:
        return <Contacts contacts={farm?.contacts} />;
      case FarmCardTabs.RATINGS:
        return <FarmRatings farmRatingsData={farmRatingsData} />;
      // case FarmCardTabs.MEMBERS:
      //   return <>{intl.formatMessage({ id: 'farms.members' })}</>;

      default:
        return null;
    }
  };

  const items = [
    {
      key: FarmCardTabs.DESCRIPTION,
      label: intl.formatMessage({ id: 'farms.description' }),
    },
    {
      key: FarmCardTabs.WORKING_TIME,
      label: intl.formatMessage({ id: 'farms.working_time' }),
    },
    {
      key: FarmCardTabs.CONTACTS,
      label: intl.formatMessage({ id: 'farms.contacts' }),
    },
    ...(isEmarket
      ? [
          {
            key: FarmCardTabs.DELIVERY,
            label: intl.formatMessage({ id: 'farms.delivery' }),
          },
        ]
      : []),
    {
      key: FarmCardTabs.GALLERY,
      label: intl.formatMessage({ id: 'farms.gallery' }),
    },
    {
      key: FarmCardTabs.PARTNERS,
      label: intl.formatMessage({ id: 'farms.partners' }),
    },
    {
      key: FarmCardTabs.RATINGS,
      label: (
        <RatingsTabLabel>
          <span>{intl.formatMessage({ id: 'farms.ratings' })}</span>
          {farmRatingsData && farmRatingsData.overall_average > 0 && (
            <StyledRate>
              <Rate
                disabled
                allowHalf
                value={farmRatingsData.overall_average}
              />
            </StyledRate>
          )}
        </RatingsTabLabel>
      ),
    },
    // {
    //   key: FarmCardTabs.MEMBERS,
    //   label: (
    //     <div className={'farm-members'}>
    //       <span>{intl.formatMessage({ id: 'farms.members' })}</span>
    //       <img alt={'quality-mark-img'} src={'../qualityMark/3-09 1.jpg'} />
    //     </div>
    //   ),
    // },
  ];

  const filterItems = useMemo(() => {
    const conditions: any = {
      [FarmCardTabs.DESCRIPTION]: !!farm?.description,

      [FarmCardTabs.WORKING_TIME]: !!farm?.farm_infrastructures?.some(
        (infrastructure) => infrastructure.status === true
      ),

      [FarmCardTabs.GALLERY]: !!farm?.images?.length,
      [FarmCardTabs.DELIVERY]: !!farm?.deliveries?.some(
        (delivery) => delivery.status === true
      ),
      [FarmCardTabs.PARTNERS]: !!farm?.partner_farms?.length,
      [FarmCardTabs.CONTACTS]: !!farm?.contacts?.some(
        (contact) => contact.status === true
      ),
      [FarmCardTabs.MEMBERS]: false,
    };

    return items.filter((item) => conditions[item.key] ?? true);
  }, [items, workingTimeData, farm]);

  const mobileRenderContent = (menuItem: string) => {
    switch (menuItem) {
      case FarmCardTabs.DESCRIPTION:
        return (
          <div className={'farm-info'}>
            <div className={'farm-img-box'}>
              <FallbackImage
                src={farm?.logo}
                type={'FarmCard'}
                preview={false}
              />
              <div className={'farm-tags'}>
                {farm?.is_top_farm && (
                  <div className={'top-farm'}>
                    {intl.formatMessage({ id: 'all_farm_card.favorite' })}
                  </div>
                )}
              </div>
              <div className="logos">
                {farm?.quality_marks?.map((qmark) => (
                  <Tooltip key={qmark.id} title={qmark.name}>
                    <span>
                      <FallbackImage
                        className={`quality-mark ${qualityMarkLogoClassName(
                          qmark.slug
                        )}`}
                        src={qmark.logo ? qmark.logo : ''}
                        type={'notFound'}
                        preview={false}
                        alt={qmark.name}
                      />
                    </span>
                  </Tooltip>
                ))}
              </div>
            </div>
            <h1>{farm?.brand ?? farm?.name}</h1>
            <div className={'mobile-farm-info'}>
              <div className={'mobile-info-div'}>
                {farm?.contact_email && (
                  <div className={'info-column'}>
                    <Icon name="sms-tracking" type={'avarage'} />
                    {farm?.contact_email}
                  </div>
                )}
                {farm?.contact_phone && (
                  <div className={'info-column'}>
                    <Icon name="call-calling" type={'avarage'} />
                    {farm?.contact_phone}
                  </div>
                )}
                {farm?.website && (
                  <div className={'info-column'}>
                    <Icon name="export" type={'avarage'} />
                    <a
                      href={farm?.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {farm?.website}
                    </a>
                  </div>
                )}
                {address.length > 0 && (
                  <div className={'info-column'}>
                    <Icon name="location-tick-icon" type={'avarage'} />
                    <div className={'address-list'}>
                      {address.map((item, index) => (
                        <span key={index}>
                          {item}
                          {index === address.length - 1 ? '' : ','}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className={'bookmark'}>
                  {(farm?.name ||
                    farm?.reg_nr ||
                    farm?.pvd_nr ||
                    farm?.pvn_nr) && (
                    <div className={'bookmark-2'}>
                      <Icon name="bookmark-2" type={'avarage'} />
                      {farm?.name} <br />
                      {farm?.reg_nr && (
                        <>
                          {intl.formatMessage({
                            id: 'farms.reg_nr_short',
                          })}
                          {farm?.reg_nr} <br />
                        </>
                      )}
                      {farm?.pvd_nr && (
                        <>
                          {intl.formatMessage({
                            id: 'farms.pvd_nr_with_colon',
                          })}
                          {farm?.pvd_nr} <br />
                        </>
                      )}
                      {farm?.pvn_nr && (
                        <>
                          {intl.formatMessage({
                            id: 'farms.pvn_nr_with_colon',
                          })}
                          {farm?.pvn_nr}
                        </>
                      )}
                    </div>
                  )}
                  <div className={'mobile-buttons'}>
                    <a href={googleUrl} target={'_blank'} rel="noreferrer">
                      <Button
                        label={intl.formatMessage({ id: 'farms.google' })}
                      />
                    </a>
                    <a href={wazeUrl} target={'_blank'} rel="noreferrer">
                      <Button
                        label={intl.formatMessage({ id: 'farms.waze' })}
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="mobile-description"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(farm?.description || ''),
              }}
            ></div>
            <div className={'mobile-farm-services-wrapper'}>
              <div className={'mobile-farm-services-title'}>
                {intl.formatMessage({ id: 'farms.services' })}
              </div>
              <div className={'mobile-farm-services-container'}>
                {farm?.services?.map((option: FarmService) => (
                  <Tag
                    className={'mobile-farm-service-tag'}
                    key={option.id}
                    bordered={true}
                  >
                    {option.name}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        );
      case FarmCardTabs.PRODUCTS:
        return (
          <>
            {!!products?.length && (
              <div className="other-products-container">
                <div className="container">
                  <p className="product-description ">
                    <FormattedMessage
                      id="farm_view.farm_produce_text"
                      values={{
                        b: (chunks) => (
                          <span className="font-weight-500">{chunks}</span>
                        ),
                      }}
                    />
                  </p>
                </div>

                <PopularProducts
                  products={products}
                  farm_name={farm?.brand ?? farm?.name}
                  showTitle={false}
                  farmView
                />
              </div>
            )}
          </>
        );
      case FarmCardTabs.WORKING_TIME:
        return (
          <div className="farm-infrastructures">
            <Infrastructures
              infrastructures={farm?.farm_infrastructures?.map(
                (infrastructure) => ({
                  ...infrastructure,
                  open_hours: infrastructure.open_hours ?? workingTimeData,
                })
              )}
              daysOfWeek={daysOfWeek}
            />
          </div>
        );
      case FarmCardTabs.DELIVERY:
        return (
          <div className="farm-delivery">
            <Deliveries deliveries={farm?.deliveries} daysOfWeek={daysOfWeek} />
          </div>
        );
      case FarmCardTabs.GALLERY:
        return <Galeries farm={farm} />;
      case FarmCardTabs.PARTNERS:
        return <Partners farm={farm} />;
      case FarmCardTabs.CONTACTS:
        return <Contacts contacts={farm?.contacts} />;
      // case FarmCardTabs.MEMBERS:
      //   return <>{intl.formatMessage({ id: 'farms.members' })}</>;
    }
  };

  const MobileItems = [
    {
      key: FarmCardTabs.DESCRIPTION,
      label: intl.formatMessage({ id: 'farms.description' }),
    },
    {
      key: FarmCardTabs.PRODUCTS,
      label: intl.formatMessage({ id: 'farms.products' }),
      className: 'menu-products',
    },
    {
      key: FarmCardTabs.WORKING_TIME,
      label: intl.formatMessage({ id: 'farms.working_time' }),
    },
    {
      key: FarmCardTabs.CONTACTS,
      label: intl.formatMessage({ id: 'farms.contacts' }),
    },
    ...(isEmarket
      ? [
          {
            key: FarmCardTabs.DELIVERY,
            label: intl.formatMessage({ id: 'farms.delivery' }),
          },
        ]
      : []),
    {
      key: FarmCardTabs.GALLERY,
      label: intl.formatMessage({ id: 'farms.gallery' }),
    },
    {
      key: FarmCardTabs.PARTNERS,
      label: intl.formatMessage({ id: 'farms.partners' }),
    },
    // {
    //   key: FarmCardTabs.MEMBERS,
    //   label: intl.formatMessage({ id: 'farms.members' }),
    // },
  ];

  const handleMenuClick = (e: MenuInfo) => {
    setSelectedMenuItem(e.key);
  };

  const secondBanner = banners.data.find(
    (banner) => banner.type === 'banner_4'
  );

  if (!farm) {
    return (
      <Spinner spinning={true} wrapperClassName="spinner">
        <Spin />
      </Spinner>
    );
  }

  return (
    <StyledFarmView>
      <SearchAndFilter rootClassName="laptop-search-section-box" />
      <div className={'mobile-menu-container'}>
        <div
          className="mobile-back"
          onClick={() =>
            navigate(
              isEmarket
                ? PublicRoute.FARMS_ALL
                : PublicRoute.WHOLESALE_FARMS_ALL
            )
          }
        >
          <Icon name="arrow-left" className="back-icon" />
          <span className="back-text">
            {intl.formatMessage({
              id: 'general.back_to_catalog',
            })}
          </span>
        </div>
        <ul className="custom-menu">
          {MobileItems.map((item) => (
            <li
              key={item.key}
              className={`custom-menu-item ${
                selectedMenuItem === item.key ? 'active' : ''
              }`}
              onClick={() => setSelectedMenuItem(item.key)}
            >
              {item.label}
            </li>
          ))}
        </ul>
        <div className={'content-section'}>
          {mobileRenderContent(selectedMenuItem)}
        </div>
      </div>
      <div className="container desktop-content">
        <div className={'farm-info'}>
          <div className={'farm-img-box'}>
            <FallbackImage
              src={farm.logo ?? farm?.images?.[0]?.url_original}
              type={'FarmCard'}
              preview={false}
            />
            {cooperativeBadge}
            <div className={'farm-tags'}>
              {farm?.is_top_farm && (
                <div className={'top-farm'}>
                  {intl.formatMessage({ id: 'all_farm_card.favorite' })}
                </div>
              )}
            </div>
          </div>
          <div className={'farm-contact'}>
            <div className={'farm-title'}>
              <h1>{farm.brand ?? farm.name}</h1>
              <div className="logos">
                {farm.quality_marks?.map((qmark) => (
                  <Tooltip key={qmark.id} title={qmark.name}>
                    <span>
                      <FallbackImage
                        className={`quality-mark ${qualityMarkLogoClassName(
                          qmark.slug
                        )}`}
                        src={qmark.logo ? qmark.logo : ''}
                        type={'notFound'}
                        preview={false}
                        alt={qmark.name}
                      />
                    </span>
                  </Tooltip>
                ))}
              </div>
              <div className={'mobile-website-box'}>
                <div className={'mobile-website'}>
                  {farm.website && (
                    <a
                      href={
                        farm.website.startsWith('http')
                          ? farm.website
                          : `https://${farm.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {farm.website}
                    </a>
                  )}
                  <div>
                    <a href={googleUrl} target={'_blank'} rel="noreferrer">
                      <Button
                        label={intl.formatMessage({ id: 'farms.google' })}
                      />
                    </a>
                    <a href={wazeUrl} target={'_blank'} rel="noreferrer">
                      <Button
                        label={intl.formatMessage({ id: 'farms.waze' })}
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className={'farm-services-container'}>
              {farm?.services?.map((option: FarmService) => (
                <Tag
                  className={'farm-service-tag'}
                  key={option.id}
                  bordered={true}
                >
                  {option.name}
                </Tag>
              ))}
            </div>
            <div className={'farm-datas'}>
              <ul>
                {farm.contact_email && (
                  <li>
                    <Icon name="red-mail" />
                    {farm.contact_email}
                  </li>
                )}
                {farm.contact_phone && (
                  <li>
                    <Icon name="red-phone" />
                    {farm.contact_phone}
                  </li>
                )}
              </ul>

              <ul>
                {address.map((item, index) => (
                  <li key={index}>
                    <span>
                      {item}
                      {index === address.length - 1 ? '' : ','}
                    </span>
                  </li>
                ))}
              </ul>

              <ul>
                {farm.name && <li>{farm.name}</li>}
                {farm.reg_nr && (
                  <li>
                    {intl.formatMessage({
                      id: 'farms.reg_nr_short',
                    })}
                    {farm.reg_nr}
                  </li>
                )}
                {farm.pvd_nr && (
                  <li>
                    {intl.formatMessage({
                      id: 'farms.pvd_nr_with_colon',
                    })}
                    {farm.pvd_nr}
                  </li>
                )}
                {farm.pvn_nr && (
                  <li>
                    {intl.formatMessage({
                      id: 'farms.pvn_nr_with_colon',
                    })}
                    {farm.pvn_nr}
                  </li>
                )}
              </ul>

              <ul>
                {farm.website && (
                  <li>
                    <a
                      href={
                        farm.website.startsWith('http')
                          ? farm.website
                          : `https://${farm.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {farm.website}
                    </a>
                  </li>
                )}
                <li>
                  <a href={googleUrl} target={'_blank'} rel="noreferrer">
                    <Button
                      label={intl.formatMessage({ id: 'farms.google' })}
                    />
                  </a>
                </li>
                <li>
                  <a href={wazeUrl} target={'_blank'} rel="noreferrer">
                    <Button label={intl.formatMessage({ id: 'farms.waze' })} />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className={'mobile-farm-datas-div'}>
        <div className={'mobile-farm-datas'}>
          <ul>
            {farm.contact_email && (
              <li>
                <Icon name="red-mail" />
                {farm.contact_email}
              </li>
            )}
            {farm.contact_phone && (
              <li>
                <Icon name="red-phone" />
                {farm.contact_phone}
              </li>
            )}
          </ul>

          <ul>
            {address.map((item, index) => (
              <li key={index}>
                <span>
                  {item}
                  {index === address.length - 1 ? '' : ','}
                </span>
              </li>
            ))}
          </ul>

          <ul>
            <li>{farm.name}</li>
            {farm.reg_nr && (
              <li>
                {intl.formatMessage({
                  id: 'farms.reg_nr_short',
                })}
                {farm.reg_nr}
              </li>
            )}
            {farm.pvd_nr && (
              <li>
                {intl.formatMessage({
                  id: 'farms.pvd_nr_with_colon',
                })}
                {farm.pvd_nr}
              </li>
            )}
            {farm.pvn_nr && (
              <li>
                {intl.formatMessage({
                  id: 'farms.pvn_nr_with_colon',
                })}
                {farm.pvn_nr}
              </li>
            )}
          </ul>

          <ul>
            {farm.website && (
              <li>
                <a
                  href={
                    farm.website.startsWith('http')
                      ? farm.website
                      : `https://${farm.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {farm.website}
                </a>
              </li>
            )}
            <li>
              <a href={googleUrl} target={'_blank'} rel="noreferrer">
                <Button label={intl.formatMessage({ id: 'farms.google' })} />
              </a>
            </li>
            <li>
              <a href={wazeUrl} target={'_blank'} rel="noreferrer">
                <Button label={intl.formatMessage({ id: 'farms.waze' })} />
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className={'menu-container'}>
        <div className="container">
          <Menu
            mode="horizontal"
            defaultSelectedKeys={[selectedMenuItem]}
            items={filterItems}
            onClick={handleMenuClick}
            style={{ flex: 1, minWidth: 0 }}
            rootClassName="farm-menu-root"
          />
        </div>
        <hr className="menu-line" />
        <div className="container">
          <div className={'content-section'}>
            {renderContent(selectedMenuItem)}
          </div>
        </div>
      </div>
      {!!farm && !!farmProducts?.data?.length && (
        <div className="desktop-products-container">
          <PopularProducts
            title={`${farm?.brand ?? farm?.name} ${intl.formatMessage({
              id: 'farms.product',
            })}`}
            products={farmProducts.data}
            farm_name={farm?.brand ?? farm?.name}
            farmView
          />
          {farmProducts && farmProducts.total > FARM_PRODUCTS_PER_PAGE && (
            <DefaultPagination
              totalRecords={farmProducts.total}
              pageSize={farmProducts.per_page}
              currentPage={farmProducts.current_page}
              onChange={(page) => onFarmProductsPageChange(page)}
            />
          )}
        </div>
      )}

      <div className="banners">
        <div className="container">
          {secondBanner && <Banner banners={[secondBanner]} />}
        </div>
      </div>
    </StyledFarmView>
  );
};

export default FarmView;
